import React, { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

// SVG icon assets — same files imported by Toolbar.js / ContextualToolPanel.js
import { ReactComponent as StopWarning } from '../../../svg/stop-svgrepo-com.svg';
import { ReactComponent as Select } from '../../../svg/cursor-svgrepo-com.min.svg';
import { ReactComponent as Highlighter } from '../../../svg/highlight-svgrepo-com.svg';
import { ReactComponent as SelectRectangle } from '../../../svg/select-rectangle-svgrepo-com.svg';
import { ReactComponent as ArrowText } from '../../../svg/arrows-expand-down-right-svgrepo-com.svg';
import { ReactComponent as Cotation } from '../../../svg/arrows-shrink-h-svgrepo-com.svg';
import { ReactComponent as DrawPolylineIcon } from '../../../svg/polyline-pt-svgrepo-com.svg';
import { ReactComponent as DrawPolygonIcon } from '../../../svg/polygon-pt-svgrepo-com.svg';
import { ReactComponent as ArrowMove } from '../../../svg/arrows-move-svgrepo-com.svg';
import { ReactComponent as Copy } from '../../../svg/grid-svgrepo-com.svg';
import { ReactComponent as ArrowRotate } from '../../../svg/arrow-clockwise-svgrepo-com.svg';
import { ReactComponent as SymmetryVertical } from '../../../svg/symmetry-vertical-svgrepo-com.svg';
import { ReactComponent as Scale } from '../../../svg/rulers-svgrepo-com.svg';
import { ReactComponent as Delete } from '../../../svg/delete-svgrepo-com.svg';
import { ReactComponent as LayerForward } from '../../../svg/layer-forward-svgrepo-com.svg';
import { ReactComponent as SidebarArrow } from '../../../svg/arrow-right-svgrepo-com.svg';
import { ReactComponent as Clipboard } from '../../../svg/clipboard-check-svgrepo-com.svg';


// Small inline SVGs mirroring icons the toolbar renders without an imported asset
const DiskMarkerIcon = () => (
  <svg
    className="shortcut-icon-svg"
    viewBox="0 0 16 16"
    width="14"
    height="14"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="4" fill="currentColor" />
  </svg>
);

// Inline SVGs mirroring DisplayKeysPanel's icons (ruler / area / overlay)
const RulerInlineIcon = () => (
  <svg
    className="shortcut-icon-svg"
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <g transform="rotate(-45 12 12)">
      <rect x="2" y="8" width="20" height="8" rx="1" />
      <path d="M6 8v3M10 8v3M14 8v3M18 8v3" />
    </g>
  </svg>
);

const AreaInlineIcon = () => (
  <svg
    className="shortcut-icon-svg"
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9l6-6M3 15l12-12M3 21l18-18M9 21l12-12M15 21l6-6" strokeWidth="1.5" opacity="0.6" />
  </svg>
);

const OverlayInlineIcon = () => (
  <svg
    className="shortcut-icon-svg"
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 20L20 4" strokeWidth="2" />
    <path d="M4 20H20" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.7" />
    <path d="M20 20V4" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.7" />
    <path d="M12 20 A 8 8 0 0 0 16 16" strokeWidth="1.5" opacity="0.8" />
  </svg>
);

// Plain-text glyph "icons" (Σ, ∪, ⊕, T) rendered in the same slot
const Glyph = ({ children, title }) => (
  <span className="shortcut-icon-glyph" title={title} aria-hidden="true">
    {children}
  </span>
);

const SHORTCUT_SECTIONS = [
  {
    title: 'Page Navigation',
    shortcuts: [
      {
        desc: 'Previous page',
        keys: 'Ctrl+←',
        tooltipKey: 'replan.pdfControls.prev_page',
        icon: () => <Glyph title="◀">◀</Glyph>,
      },
      {
        desc: 'Next page',
        keys: 'Ctrl+→',
        tooltipKey: 'replan.pdfControls.next_page',
        icon: () => <Glyph title="▶">▶</Glyph>,
      },
      { desc: 'Browse all pages', keys: 'Ctrl+Enter', tooltipKey: 'replan.pdfControls.open_page_selector', icon: null },
      { desc: 'Focus search', keys: 'Ctrl+F', tooltipKey: 'replan.pdfControls.search_placeholder', icon: null },
      {
        desc: 'Save drawings',
        keys: 'Ctrl+S',
        tooltipKey: 'replan.pdfControls.save_drawings',
        icon: () => <Glyph title="💾">💾</Glyph>,
      },
      {
        desc: 'Open shortcut help',
        keys: '?',
        icon: () => <Glyph title="?">?</Glyph>,
      },
    ],
  },
  {
    title: 'Drawing Tools',
    shortcuts: [
      { desc: 'Stop / Deselect', keys: 'Q', tooltipKey: 'replan.toolbar.stop_desc', icon: StopWarning },
      { desc: 'Select / Pan', keys: 'Space', tooltipKey: 'replan.toolbar.select_pan_desc', icon: Select },
      { desc: 'Highlighter', keys: 'H', tooltipKey: 'replan.toolbar.pen_desc', icon: Highlighter },
      { desc: 'Take Note', keys: 'N', tooltipKey: 'replan.toolbar.rectangle_desc', icon: SelectRectangle },
      {
        desc: 'Text Label',
        keys: 'T',
        tooltipKey: 'replan.toolbar.text_label_desc',
        icon: () => <Glyph title="T">T</Glyph>,
      },
      { desc: 'Arrow', keys: 'W', tooltipKey: 'replan.toolbar.arrow_desc', icon: ArrowText },
      { desc: 'Measure Cotation', keys: 'K', tooltipKey: 'replan.toolbar.cotation_desc', icon: Cotation },
      {
        desc: 'Add Point',
        keys: 'P',
        tooltipKey: 'replan.toolbar.add_point_desc',
        icon: DiskMarkerIcon,
      },
      { desc: 'Polyline', keys: 'L', tooltipKey: 'replan.toolbar.polyline_desc', icon: DrawPolylineIcon },
      { desc: 'Polygon', keys: 'S', tooltipKey: 'replan.toolbar.polygon_desc', icon: DrawPolygonIcon },
      {
        desc: 'Measurement Summary',
        keys: 'U',
        tooltipKey: 'replan.toolbar.create_summary_desc',
        icon: () => <Glyph title="Σ">Σ</Glyph>,
      },
    ],
  },
  {
    title: 'Modes & Toggles',
    shortcuts: [
      { desc: 'Orthogonal Mode', keys: 'O', tooltipKey: 'replan.toolbar.orthogonal_mode_desc', icon: null },
      { desc: 'Rectangle Mode', keys: 'R', tooltipKey: 'replan.toolbar.rectangle_mode_desc', icon: null },
      { desc: 'Snapping', keys: 'X', tooltipKey: 'replan.toolbar.snapping_mode_desc', icon: null },
      { desc: 'Grid', keys: 'G', tooltipKey: 'replan.toolbar.grid_mode_desc', icon: null },
      {
        desc: 'Undo',
        keys: 'Ctrl+Z',
        tooltipKey: 'replan.pdfControls.undo',
        icon: () => <Glyph title="↺">↺</Glyph>,
      },
      {
        desc: 'Redo',
        keys: 'Ctrl+Shift+Z',
        tooltipKey: 'replan.pdfControls.redo',
        icon: () => <Glyph title="↻">↻</Glyph>,
      },
      {
        desc: 'Cancel / Deselect',
        keys: 'Escape',
        icon: StopWarning,
      },
      {
        desc: 'Decrease stroke width',
        keys: '-',
        icon: Scale,
      },
      {
        desc: 'Increase stroke width',
        keys: '+',
        icon: Scale,
      },
    ],
  },
  {
    title: 'Display Settings',
    shortcuts: [
      {
        desc: 'Toggle Metric / Imperial',
        keys: 'M',
        icon: () => <Glyph title="M">M</Glyph>,
      },
      {
        desc: 'Cycle Measurements',
        keys: 'J',
        icon: RulerInlineIcon,
      },
      {
        desc: 'Toggle Area',
        keys: 'A',
        icon: AreaInlineIcon,
      },
      {
        desc: 'Toggle Overlay',
        keys: 'E',
        icon: OverlayInlineIcon,
      },
      {
        desc: 'Display Settings',
        keys: 'D',
        tooltipKey: 'replan.pdfControls.display_settings_desc',
        icon: () => <Glyph title="☰">☰</Glyph>,
      },
    ],
  },
  {
    title: 'Shape Operations',
    shortcuts: [
      { desc: 'Move shapes', keys: 'V', tooltipKey: 'replan.contextualToolPanel.move_desc', icon: ArrowMove },
      { desc: 'Copy / Duplicate', keys: 'C', tooltipKey: 'replan.contextualToolPanel.copy_desc', icon: Copy },
      { desc: 'Rotate shapes', keys: 'R', tooltipKey: 'replan.contextualToolPanel.rotate_desc', icon: ArrowRotate },
      { desc: 'Symmetry / Flip', keys: 'F', tooltipKey: 'replan.contextualToolPanel.symmetry_desc', icon: SymmetryVertical },
      { desc: 'Scale shapes', keys: 'Z', tooltipKey: 'replan.contextualToolPanel.scale_desc', icon: Scale },
      { desc: 'Delete shapes', keys: 'Del', tooltipKey: 'replan.contextualToolPanel.delete_desc', icon: Delete },
      { desc: 'Bring to Front', keys: ']', tooltipKey: 'replan.contextualToolPanel.bring_front_desc', icon: LayerForward },
      {
        desc: 'Send to Back',
        keys: '[',
        tooltipKey: 'replan.contextualToolPanel.send_back_desc',
        icon: () => (
          <span className="shortcut-icon-svg-wrap shortcut-icon-flip">
            <LayerForward className="shortcut-icon-svg" />
          </span>
        ),
      },
      {
        desc: 'Union (merge)',
        keys: 'U',
        tooltipKey: 'replan.contextualToolPanel.union_desc',
        icon: () => <Glyph title="∪">∪</Glyph>,
      },
      {
        desc: 'Difference (XOR)',
        keys: 'X',
        tooltipKey: 'replan.contextualToolPanel.difference_desc',
        icon: () => <Glyph title="⊕">⊕</Glyph>,
      },
    ],
  },
  {
    title: 'Panels',
    shortcuts: [
      {
        desc: 'Toggle Shape List',
        keys: '*',
        icon: SidebarArrow,
      },
      { desc: 'Open Project Selector', keys: 'Alt+Enter', tooltipKey: 'replan.pdfControls.open_page_selector', icon: null },
    ],
  },
  {
    title: 'Clipboard',
    shortcuts: [
      { desc: 'Copy selected shapes', keys: 'Ctrl+C', icon: Copy },
      { desc: 'Cut selected shapes', keys: 'Ctrl+X', icon: Delete },
      { desc: 'Paste copied shapes', keys: 'Ctrl+V', icon: Clipboard },
      {
        desc: 'Redo',
        keys: 'Ctrl+Y',
        icon: ArrowRotate,
      },
    ],
  },
];

/**
 * Extract the first line from a tooltip translation (tooltips often use \n
 * to separate description / label / shortcut into multi-line tooltips).
 */
const firstLine = (text) => {
  if (!text) return '';
  const idx = text.indexOf('\n');
  return idx === -1 ? text : text.slice(0, idx);
};

const ShortcutReferenceModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const modalRef = useRef(null);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="shortcut-modal-backdrop" onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className="shortcut-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shortcut-modal-header">
          <h2>{t('shortcuts.title', 'Keyboard Shortcuts')}</h2>
          <button className="shortcut-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="shortcut-modal-body">
          {SHORTCUT_SECTIONS.map((section) => {
            const slug = section.title.replace(/\s+/g, '_').toLowerCase();
            return (
              <div key={section.title} className="shortcut-section">
                <h3>
                  {t(`shortcuts.${slug}`, section.title)}
                </h3>
                <div className="shortcut-grid">
                  {section.shortcuts.map((s) => {
                    const tooltipText = s.tooltipKey
                      ? firstLine(t(s.tooltipKey))
                      : '';
                    const descSlug = s.desc
                      .toLowerCase()
                      .replace(/[\s/()]+/g, '_')
                      .replace(/^_+|_+$/g, '');
                    const displayDesc =
                      tooltipText ||
                      t(`shortcuts.${descSlug}`, s.desc);
                    const Icon = s.icon;
                    const hasIcon =
                      Icon !== null &&
                      Icon !== undefined &&
                      (typeof Icon === 'function' || typeof Icon === 'object');
                    return (
                      <div
                        key={s.keys}
                        className="shortcut-row"
                        title={tooltipText || undefined}
                      >
                        <span className="shortcut-icon" aria-hidden={hasIcon ? undefined : 'true'}>
                          {hasIcon ? <Icon className="shortcut-icon-svg" /> : null}
                        </span>
                        <span className="shortcut-desc">{displayDesc}</span>
                        <span className="shortcut-keys">{s.keys}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ShortcutReferenceModal;
